import { CoreString } from "../../../../core/String";
import { NodeEvent } from "../../../poly/NodeEvent";
import { CoreNodeSelection } from "../../../../core/NodeSelection";
import { Poly } from "../../../Poly";
import { SopOperationContainer } from "../../../operations/container/sop";
import { MapUtils } from "../../../../core/MapUtils";
import { NameController } from "../NameController";
export class HierarchyChildrenController {
  constructor(node, _context) {
    this.node = node;
    this._context = _context;
    this._childrenByName = /* @__PURE__ */ new Map();
    this._childrenByType = /* @__PURE__ */ new Map();
    this._childrenAndGrandchildrenByContext = /* @__PURE__ */ new Map();
  }
  get selection() {
    return this._selection = this._selection || new CoreNodeSelection(this.node);
  }
  dispose() {
    const children = this.children();
    for (let child of children) {
      this.node.removeNode(child);
    }
    this._selection = void 0;
  }
  get context() {
    return this._context;
  }
  setOutputNodeFindMethod(method) {
    this._output_node_find_method = method;
  }
  outputNode() {
    if (this._output_node_find_method) {
      return this._output_node_find_method();
    }
  }
  setChildName(node, newName) {
    let currentChildWithName;
    newName = CoreString.sanitizeName(newName);
    if ((currentChildWithName = this._childrenByName.get(newName)) != null) {
      if (node.name() === newName && currentChildWithName.graphNodeId() === node.graphNodeId()) {
        return;
      }
      newName = CoreString.increment(newName);
      return this.setChildName(node, newName);
    } else {
      const currentName = node.name();
      const currentChild = this._childrenByName.get(currentName);
      if (currentChild) {
        this._childrenByName.delete(currentName);
      }
      this._childrenByName.set(newName, node);
      node.nameController.updateNameFromParent(newName);
      this._addToNodesByType(node);
      this.node.scene().nodesController.addToInstanciatedNode(node);
    }
  }
  _nextAvailableChildName(nodeName) {
    nodeName = CoreString.sanitizeName(nodeName);
    return this._childrenByName.get(nodeName) ? this._nextAvailableChildName(CoreString.increment(nodeName)) : nodeName;
  }
  nodeContextSignature() {
    return `${this.node.context()}/${this.node.type()}`;
  }
  availableChildrenClasses() {
    return Poly.registeredNodes(this.node);
  }
  isValidChildType(node_type) {
    const node_class = this.availableChildrenClasses()[node_type];
    return node_class != null;
  }
  createNode(nodeClassOrString, options) {
    if (typeof nodeClassOrString == "string") {
      const nodeClass = this._findNodeClass(nodeClassOrString);
      return this._createAndInitNode(nodeClass, options);
    } else {
      return this._createAndInitNode(nodeClassOrString, options);
    }
  }
  _createAndInitNode(nodeClass, options) {
    const requestedNodeName = (options == null ? void 0 : options.nodeName) || NameController.baseName(nodeClass);
    const nodeName = this._nextAvailableChildName(requestedNodeName);
    const childNode = new nodeClass(this.node.scene(), nodeName, options);
    childNode.initializeBaseAndNode();
    this._addNode(childNode);
    childNode.lifecycle.setCreationCompleted();
    return childNode;
  }
  _findNodeClass(node_type) {
    const nodeClass = this.availableChildrenClasses()[node_type.toLowerCase()];
    if (nodeClass == null) {
      const message = `child node type '${node_type}' not found for node '${this.node.path()}'. Available types are: ${Object.keys(this.availableChildrenClasses()).join(", ")}, ${this._context}, ${this.node.type()}`;
      console.error(message);
      throw message;
    }
    return nodeClass;
  }
  createOperationContainer(operation_type, operation_container_name, options) {
    const operation_class = Poly.registeredOperation(this._context, operation_type);
    if (operation_class == null) {
      const message = `no operation found with context ${this._context}/${operation_type}`;
      console.error(message);
      throw message;
    } else {
      const operation = new operation_class(this.node.scene());
      const operation_container = new SopOperationContainer(operation, operation_container_name, (options == null ? void 0 : options.paramsInitValueOverrides) || {});
      return operation_container;
    }
  }
  _addNode(child_node) {
    child_node.setParent(this.node);
    child_node.params.init();
    child_node.parentController.onSetParent();
    child_node.nameController.runPostSetFullPathHooks();
    if (child_node.childrenAllowed() && child_node.childrenController) {
      for (let child of child_node.childrenController.children()) {
        child.nameController.runPostSetFullPathHooks();
      }
    }
    this.node.emit(NodeEvent.CREATED, { child_node_json: child_node.toJSON() });
    if (this.node.scene().lifecycleController.onAfterCreatedCallbackAllowed()) {
      child_node.lifecycle.runOnAfterCreatedCallbacks();
    }
    child_node.lifecycle.runOnAfterAddedCallbacks();
    this.node.lifecycle.runOnChildAddCallbacks(child_node);
    if (child_node.require_webgl2()) {
      this.node.scene().webgl_controller.set_require_webgl2();
    }
    this.node.scene().missingExpressionReferencesController.checkForMissingNodeReferences(child_node);
    return child_node;
  }
  removeNode(childNode) {
    if (childNode.parent() != this.node) {
      return console.warn(`node ${childNode.name()} not under parent ${this.node.path()}`);
    } else {
      childNode.lifecycle.runOnBeforeDeleteCallbacks();
      if (this.selection.contains(childNode)) {
        this.selection.remove([childNode]);
      }
      const first_connection = childNode.io.connections.firstInputConnection();
      const input_connections = childNode.io.connections.inputConnections();
      const output_connections = childNode.io.connections.outputConnections();
      if (input_connections) {
        for (let input_connection of input_connections) {
          if (input_connection) {
            input_connection.disconnect({ setInput: true });
          }
        }
      }
      if (output_connections) {
        for (let output_connection of output_connections) {
          if (output_connection) {
            output_connection.disconnect({ setInput: true });
            if (first_connection) {
              const old_src = first_connection.node_src;
              const old_output_index = output_connection.output_index;
              const old_dest = output_connection.node_dest;
              const old_input_index = output_connection.input_index;
              old_dest.io.inputs.setInput(old_input_index, old_src, old_output_index);
            }
          }
        }
      }
      childNode.setParent(null);
      this._childrenByName.delete(childNode.name());
      this._removeFromNodesByType(childNode);
      this.node.scene().nodesController.removeFromInstanciatedNode(childNode);
      childNode.setSuccessorsDirty(this.node);
      childNode.graphDisconnectSuccessors();
      this.node.lifecycle.runOnChildRemoveCallbacks(childNode);
      childNode.lifecycle.runOnDeleteCallbacks();
      childNode.dispose();
      childNode.emit(NodeEvent.DELETED, { parent_id: this.node.graphNodeId() });
    }
  }
  _addToNodesByType(node) {
    const nodeId = node.graphNodeId();
    const type = node.type();
    MapUtils.addToSetAtEntry(this._childrenByType, type, nodeId);
    this._addToChildrenAndGrandchildrenByContext(node);
  }
  _removeFromNodesByType(node) {
    const nodeId = node.graphNodeId();
    const type = node.type();
    MapUtils.removeFromSetAtEntry(this._childrenByType, type, nodeId);
    this._removeFromChildrenAndGrandchildrenByContext(node);
  }
  _addToChildrenAndGrandchildrenByContext(node) {
    var _a;
    const nodeId = node.graphNodeId();
    const nodeContext = node.context();
    MapUtils.addToSetAtEntry(this._childrenAndGrandchildrenByContext, nodeContext, nodeId);
    const parent = this.node.parent();
    if (parent && parent.childrenAllowed()) {
      (_a = parent.childrenController) == null ? void 0 : _a._addToChildrenAndGrandchildrenByContext(node);
    }
  }
  _removeFromChildrenAndGrandchildrenByContext(node) {
    var _a;
    const nodeId = node.graphNodeId();
    const type = node.context();
    MapUtils.removeFromSetAtEntry(this._childrenAndGrandchildrenByContext, type, nodeId);
    const parent = this.node.parent();
    if (parent && parent.childrenAllowed()) {
      (_a = parent.childrenController) == null ? void 0 : _a._removeFromChildrenAndGrandchildrenByContext(node);
    }
  }
  nodesByType(type) {
    const nodeIds = this._childrenByType.get(type);
    if (!nodeIds) {
      return [];
    }
    const graph = this.node.scene().graph;
    const nodes = [];
    for (let node_id of nodeIds) {
      const node = graph.nodeFromId(node_id);
      if (node) {
        nodes.push(node);
      }
    }
    return nodes;
  }
  childByName(name) {
    return this._childrenByName.get(name) || null;
  }
  hasChildrenAndGrandchildrenWithContext(context) {
    return this._childrenAndGrandchildrenByContext.get(context) != null;
  }
  children() {
    const nodes = [];
    this._childrenByName.forEach((node) => {
      nodes.push(node);
    });
    return nodes;
  }
  childrenNames() {
    const names = [];
    this._childrenByName.forEach((node, nodeName) => {
      names.push(nodeName);
    });
    return names;
  }
  traverseChildren(callback) {
    this._childrenByName.forEach((childNode) => {
      var _a;
      callback(childNode);
      (_a = childNode.childrenController) == null ? void 0 : _a.traverseChildren(callback);
    });
  }
}
